import React, { useState, Fragment, useEffect } from 'react';
import { 
  Clock, 
  Mic, 
  MessageSquare, 
  FileText,
  Flag,
  Award,
  CheckCircle,
  XCircle,
  Gavel,
  Pause,
  PlayCircle,
  X,
  Edit,
  Send,
  Vote,
  User
} from 'lucide-react';

const ModelCongressLayout = ({ 
  format, 
  topic, 
  stage, 
  stageCode,
  activeSpeaker,
  duration,
  stageType,
  timer, 
  log, 
  message, 
  setMessage, 
  sendTurn, 
  loading, 
  error,
  selectedBill,
  selectedCommittee,
  delegates = [],
  isPaused = false,
  onPauseToggle,
  onEndDebate,
  onProposeMotion,
  onRaisePoint
}) => {
  
  // PMC Procedural Phases
  const [currentPhase, setCurrentPhase] = useState('motion-to-consider'); // motion-to-consider, structured-debate, general-debate, amendments, voting
  const [structuredDebateSpeech, setStructuredDebateSpeech] = useState(0); // 0-3 (Pro 4min, Con 4min, Pro 2min, Con 2min)
  const [amendments, setAmendments] = useState([]);
  const [showAmendmentForm, setShowAmendmentForm] = useState(false);
  const [amendmentText, setAmendmentText] = useState('');
  const [showMotionModal, setShowMotionModal] = useState(false);
  const [showPointModal, setShowPointModal] = useState(false);
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [voteResult, setVoteResult] = useState(null);
  const [chairMessages, setChairMessages] = useState([]);
  const [pendingMotion, setPendingMotion] = useState(null); // Motion waiting for second/vote
  const [motionSeconded, setMotionSeconded] = useState(false);
  const [motionVotes, setMotionVotes] = useState({ yea: 0, nay: 0, abstain: 0 });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Add chair message to log
  const addChairMessage = (message) => {
    const chairEntry = {
      role: 'chair',
      text: message,
      type: 'chair-announcement',
      timestamp: new Date().toISOString()
    };
    setChairMessages(prev => [...prev, chairEntry]);
    // Also add to log if log is controlled here, or trigger a callback
  };

  // PMC Motions (based on PMC rules)
  const pmcMotions = [
    { 
      id: 'motion-to-consider', 
      name: 'Motion to Consider', 
      requires: 'Second + 1/3 vote', 
      description: 'Brings a specific bill/resolution to the floor for debate. Requires a second. If author is present, they become sponsor and may give first pro speech. Passage requires 1/3 vote.',
      availableIn: ['motion-to-consider']
    },
    { 
      id: 'motion-to-amend', 
      name: 'Motion to Amend', 
      requires: 'Second + Majority', 
      description: 'Change a bill or resolution in a manner consistent with the preamble. If friendly, becomes part of bill. If unfriendly, moves to structured debate on amendment.',
      availableIn: ['general-debate', 'amendments']
    },
    { 
      id: 'motion-to-caucus', 
      name: 'Motion to Caucus', 
      requires: 'Majority', 
      description: 'Calls for a short time period (not to exceed 10 minutes) to informally discuss current issues. Delegate must state desired amount of time.',
      availableIn: ['general-debate', 'amendments']
    },
    { 
      id: 'previous-question', 
      name: 'Motion for the Previous Question', 
      requires: '2/3 majority', 
      description: 'Ends debate on a topic. One member may speak against the motion. A vote on the issue immediately follows passage.',
      availableIn: ['general-debate', 'amendments', 'structured-debate']
    },
    { 
      id: 'divide-question', 
      name: 'Motion to Divide the Question', 
      requires: 'Majority', 
      description: 'Allows separate votes on different sections of a bill/resolution. Can be made after motion for previous question passes but before vote.',
      availableIn: ['general-debate', 'amendments']
    }
  ];

  // PMC Points (based on PMC rules)
  const pmcPoints = [
    { 
      id: 'parliamentary-inquiry', 
      name: 'Point of Parliamentary Inquiry', 
      description: 'Request clarification of committee procedures. Can be raised during consideration of any matter, except during address of another delegate. Chair answers in accordance with rules.',
      availableIn: ['all']
    },
    { 
      id: 'information', 
      name: 'Point of Information', 
      description: 'Factual question regarding matter under discussion. Made when no one has floor or during question period of speech. Addressed to Chair, who may refer to delegate. May not relate to procedure or express opinion. One follow-up question allowed at Chair\'s discretion.',
      availableIn: ['all']
    },
    { 
      id: 'inquiry', 
      name: 'Point of Inquiry', 
      description: 'Question for speaker about topic of discussion, after speaker has yielded to questions. Used to solicit speaker\'s opinion. Must be phrased in third person through Chair. One follow-up question allowed at Chair\'s discretion.',
      availableIn: ['all']
    }
  ];

  // Chair language for motions (PMC rules)
  const getChairMotionLanguage = (motion) => {
    const language = {
      'motion-to-consider': 'A motion to consider the bill has been moved. This motion requires a second. If the bill\'s author is present, they become the sponsor and may give the first pro speech. Passage requires a 1/3 vote. Is there a second?',
      'motion-to-amend': 'A motion to amend has been moved. The delegate must provide the written amendment. The Chair will read the proposed amendment aloud and ask the bill\'s author if the amendment is friendly or unfriendly. Is there a second?',
      'motion-to-caucus': 'A motion to caucus has been moved. This motion requires a second and a majority vote. The delegate must state the desired amount of time (not to exceed 10 minutes). Is there a second?',
      'previous-question': 'The motion for the previous question has been moved. This motion requires a second and a 2/3 majority vote. One member may speak against the motion. If passed, debate will end immediately and we will proceed to voting. Is there a second?',
      'divide-question': 'A motion to divide the question has been moved. This motion requires a second and a majority vote. This motion may be made after the motion for the previous question passes but before a vote is taken. If passed, sections of the bill may be voted on separately. Is there a second?'
    };
    return language[motion.id] || `A motion for ${motion.name} has been moved. Is there a second?`;
  };

  // Chair language for points
  const getChairPointLanguage = (point) => {
    const language = {
      'personal-privilege': 'Point of personal privilege is recognized. The delegate may proceed.',
      'order': 'Point of order is recognized. The Chair will rule on the procedural matter.',
      'parliamentary-inquiry': 'Point of parliamentary inquiry is recognized. The Chair will answer the question.',
      'information': 'Point of information is recognized. The delegate may ask a factual question.',
      'inquiry': 'Point of inquiry is recognized. The delegate may ask a question to the current speaker.'
    };
    return language[point.id] || `Point of ${point.name} is recognized.`;
  };

  // Handle motion with chair announcement (PMC rules)
  const handleMotion = (motion) => {
    setSelectedMotion(motion);
    const chairMessage = getChairMotionLanguage(motion);
    addChairMessage(chairMessage);
    
    // Set pending motion that needs a second and vote
    setPendingMotion(motion);
    setMotionSeconded(false);
    setMotionVotes({ yea: 0, nay: 0, abstain: 0 });
    
    if (onProposeMotion) {
      onProposeMotion(motion);
    }
    setShowMotionModal(false);
  };

  // Handle seconding a motion
  const handleSecondMotion = () => {
    if (pendingMotion) {
      setMotionSeconded(true);
      addChairMessage('The motion has been seconded. The Chair will now call for a vote.');
      // Auto-advance to voting after a short delay
      setTimeout(() => {
        addChairMessage(`The committee will now vote on the motion: ${pendingMotion.name}.`);
      }, 1500);
    }
  };

  // Handle voting on motion
  const handleVoteOnMotion = (vote) => {
    if (pendingMotion && motionSeconded) {
      setMotionVotes(prev => ({
        ...prev,
        [vote]: prev[vote] + 1
      }));
      
      // Check if motion passes (simplified - in real scenario would need actual vote counts)
      const totalVotes = motionVotes.yea + motionVotes.nay + motionVotes.abstain + 1;
      const yeaVotes = motionVotes.yea + (vote === 'yea' ? 1 : 0);
      const requiredMajority = pendingMotion.requires.includes('2/3') 
        ? Math.ceil(totalVotes * 2 / 3)
        : pendingMotion.requires.includes('1/3')
        ? Math.ceil(totalVotes / 3)
        : Math.ceil(totalVotes / 2);
      
      if (yeaVotes >= requiredMajority) {
        // Motion passes
        setTimeout(() => {
          addChairMessage(`The motion for ${pendingMotion.name} has passed.`);
          executeMotion(pendingMotion);
          setPendingMotion(null);
          setMotionSeconded(false);
        }, 2000);
      } else {
        addChairMessage(`You voted ${vote === 'yea' ? 'Yea' : vote === 'nay' ? 'Nay' : 'Abstain'} on the motion.`);
      }
    }
  };

  // Execute motion after it passes
  const executeMotion = (motion) => {
    if (motion.id === 'motion-to-consider') {
      addChairMessage('The motion to consider has passed. The sponsor may now give the first pro speech. We will now begin structured debate.');
      setCurrentPhase('structured-debate');
      setStructuredDebateSpeech(0);
    } else if (motion.id === 'motion-to-amend') {
      addChairMessage('The motion to amend requires a written amendment. Please submit your amendment in writing to the Chair.');
    } else if (motion.id === 'motion-to-caucus') {
      addChairMessage('The motion to caucus requires the delegate to state the desired amount of time (not to exceed 10 minutes).');
    } else if (motion.id === 'previous-question' && currentPhase !== 'voting') {
      addChairMessage('The motion for the previous question has passed. Debate has ended. We will now proceed to voting on the bill.');
      setCurrentPhase('voting');
      setIsVoting(true);
    } else if (motion.id === 'divide-question') {
      addChairMessage('The motion to divide the question allows separate votes on different sections of the bill.');
    }
  };

  // Handle point with chair announcement
  const handlePoint = (point) => {
    setSelectedPoint(point);
    const chairMessage = getChairPointLanguage(point);
    addChairMessage(chairMessage);
    
    if (onRaisePoint) {
      onRaisePoint(point);
    }
    setShowPointModal(false);
  };

  // Handle structured debate completion
  useEffect(() => {
    if (currentPhase === 'structured-debate' && structuredDebateSpeech >= 3) {
      // All 4 speeches completed (0-3 = 4 speeches)
      setTimeout(() => {
        setCurrentPhase('general-debate');
        addChairMessage('Structured debate has now concluded. We will now move on to general debate. Are there any points or motions on the floor?');
      }, 1000);
    }
  }, [structuredDebateSpeech, currentPhase]);

  // Handle amendment submission
  const handleSubmitAmendment = () => {
    if (amendmentText.trim()) {
      const newAmendment = {
        id: Date.now(),
        text: amendmentText,
        status: 'pending',
        submittedBy: 'user'
      };
      setAmendments([...amendments, newAmendment]);
      addChairMessage(`An amendment has been submitted: "${amendmentText.substring(0, 50)}${amendmentText.length > 50 ? '...' : ''}". The sponsor will be asked if this amendment is friendly or unfriendly.`);
      setAmendmentText('');
      setShowAmendmentForm(false);
    }
  };

  // Handle speech completion in structured debate
  const handleStructuredSpeechComplete = () => {
    if (structuredDebateSpeech < 3) {
      setStructuredDebateSpeech(structuredDebateSpeech + 1);
      if (structuredDebateSpeech === 0) {
        addChairMessage('The first pro speech has concluded. We will now hear the first con speech.');
      } else if (structuredDebateSpeech === 1) {
        addChairMessage('The first con speech has concluded. We will now hear the second pro speech.');
      } else if (structuredDebateSpeech === 2) {
        addChairMessage('The second pro speech has concluded. We will now hear the final con speech.');
      }
    }
  };

  const getPhaseLabel = () => {
    switch (currentPhase) {
      case 'motion-to-consider':
        return 'Motion to Consider';
      case 'structured-debate':
        return 'Structured Debate';
      case 'general-debate':
        return 'General Debate';
      case 'amendments':
        return 'Amendment Debate';
      case 'voting':
        return 'Voting';
      default:
        return 'Debate';
    }
  };

  const getStructuredDebateLabel = () => {
    const labels = [
      'Pro Speech (4 min)',
      'Con Speech (4 min)',
      'Pro Speech (2 min)',
      'Con Speech (2 min)'
    ];
    return labels[structuredDebateSpeech] || '';
  };

  const billContent = selectedBill?.content || selectedBill?.text || selectedBill?.body || selectedBill?.summary || (selectedBill ? JSON.stringify(selectedBill, null, 2) : 'No bill selected. Please select a bill to debate.');

  // Combine log with chair messages
  const combinedLog = [...log, ...chairMessages];

  return (
    <div className="bg-white dark:bg-gray-800 relative flex flex-col h-full w-full">
      {/* Pause Overlay */}
      {isPaused && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col items-center gap-4 z-[60]">
            <Pause className="w-16 h-16 text-yellow-600 dark:text-yellow-500" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Debate Paused</h3>
            <p className="text-gray-600 dark:text-gray-400">The debate timer has been paused. Click "Resume Debate" to continue.</p>
            {onPauseToggle && (
              <button
                onClick={onPauseToggle}
                className="mt-4 flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-md font-medium"
              >
                <PlayCircle className="w-5 h-5" />
                Resume Debate
              </button>
            )}
          </div>
        </div>
      )}

      {/* Compact Header Section */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          {/* Left: Committee & Phase */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
              {selectedCommittee || 'Committee'}
            </span>
            <span className="text-xs font-medium text-primary-600 dark:text-primary-400 px-2 py-0.5 bg-primary-50 dark:bg-primary-900/20 rounded">
              {getPhaseLabel()}
            </span>
          </div>
          
          {/* Center: Title */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Gavel className="w-4 h-4 text-red-600 dark:text-red-400" />
            <div className="text-center">
              <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">AI Debate - Model Congress</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Princeton Model Congress Procedure</div>
            </div>
          </div>
          
          {/* Right: Timer and Buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {duration && (
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Time</div>
                <div className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatTime(timer)} /{formatTime(duration)}</div>
              </div>
            )}
            {onPauseToggle && (
              <button
                onClick={onPauseToggle}
                className="flex items-center gap-1.5 bg-yellow-600 hover:bg-yellow-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
              >
                {isPaused ? (
                  <>
                    <PlayCircle className="w-3.5 h-3.5" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="w-3.5 h-3.5" />
                    Pause Debate
                  </>
                )}
              </button>
            )}
            {onEndDebate && (
              <button
                onClick={onEndDebate}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
              >
                <X className="w-3.5 h-3.5" />
                End Debate
              </button>
            )}
          </div>
        </div>

        {/* Phase-Specific Indicator */}
        {currentPhase === 'structured-debate' && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {getStructuredDebateLabel()}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Speech {structuredDebateSpeech + 1} of 4
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Split Screen: Bill (Left) and Debate (Right) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Bill Display */}
        <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Current Bill (One Bill Per Session)
                </h3>
                {selectedBill?.title && (
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-xs block mt-1">
                    {selectedBill.title}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-xs text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
                {billContent}
              </pre>
            </div>
          </div>

          {/* Amendments Section */}
          {amendments.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Amendments</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {amendments.map((amendment) => (
                  <div key={amendment.id} className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between">
                      <span className="text-gray-700 dark:text-gray-300 flex-1">{amendment.text}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                        amendment.status === 'adopted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                        amendment.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                      }`}>
                        {amendment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel: Debate Interface */}
        <div className="w-1/2 flex flex-col">
          {/* Pending Motion Banner */}
          {pendingMotion && (
            <div className="border-b border-yellow-300 dark:border-yellow-600 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/30">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                    Motion on the Floor: {pendingMotion.name}
                  </div>
                  <div className="text-xs text-yellow-700 dark:text-yellow-300">
                    Requires: {pendingMotion.requires}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!motionSeconded ? (
                    <button
                      onClick={handleSecondMotion}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md"
                    >
                      Second Motion
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-700 dark:text-green-300 font-medium">Seconded</span>
                      <button
                        onClick={() => handleVoteOnMotion('yea')}
                        className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded hover:bg-green-200 dark:hover:bg-green-800"
                      >
                        Yea
                      </button>
                      <button
                        onClick={() => handleVoteOnMotion('nay')}
                        className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs rounded hover:bg-red-200 dark:hover:bg-red-800"
                      >
                        Nay
                      </button>
                      <button
                        onClick={() => handleVoteOnMotion('abstain')}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        Abstain
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setPendingMotion(null);
                      setMotionSeconded(false);
                      setMotionVotes({ yea: 0, nay: 0, abstain: 0 });
                    }}
                    className="text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Motion/Point Buttons - Show based on phase */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700 flex items-center gap-2 flex-wrap">
            {/* Points available in all phases */}
            <button
              onClick={() => setShowPointModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-xs font-medium hover:bg-green-200 dark:hover:bg-green-800"
            >
              <Award className="w-3.5 h-3.5" />
              Point
            </button>
            
            {/* Motion button - available in all phases except voting */}
            {currentPhase !== 'voting' && (
              <button
                onClick={() => setShowMotionModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <Flag className="w-3.5 h-3.5" />
                Motion
              </button>
            )}
            
            {/* Amendment button - only in general debate */}
            {currentPhase === 'general-debate' && (
              <button
                onClick={() => setShowAmendmentForm(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded text-xs font-medium hover:bg-purple-200 dark:hover:bg-purple-800"
              >
                <Edit className="w-3.5 h-3.5" />
                Propose Amendment
              </button>
            )}
            
            {/* Voting button in general debate or amendments */}
            {(currentPhase === 'general-debate' || currentPhase === 'amendments') && (
              <button
                onClick={() => {
                  setIsVoting(true);
                  setCurrentPhase('voting');
                  addChairMessage('The committee will now proceed to voting on the bill. All delegates please cast your votes.');
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs font-medium hover:bg-red-200 dark:hover:bg-red-800"
              >
                <Vote className="w-3.5 h-3.5" />
                Vote
              </button>
            )}
          </div>

          {/* All Delegates Panel */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">All Delegates ({delegates.length})</span>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {log.filter(entry => entry.role === 'bot' && entry.delegateName).length} have spoken
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {delegates.length > 0 ? (
                delegates.map((delegate, idx) => {
                  const hasSpoken = log.some(entry => entry.role === 'bot' && entry.delegateName === delegate.name);
                  return (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs whitespace-nowrap transition-all ${
                        hasSpoken 
                          ? 'bg-purple-200 dark:bg-purple-800/50 border border-purple-400 dark:border-purple-600' 
                          : 'bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 opacity-60'
                      }`}
                      title={`${delegate.name} (${delegate.state}) - ${delegate.position}`}
                    >
                      <User className={`w-3 h-3 ${hasSpoken ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      <span className={`${hasSpoken ? 'text-purple-800 dark:text-purple-300 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                        {delegate.name}
                      </span>
                    </div>
                  );
                })
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400 italic">No delegates loaded</span>
              )}
            </div>
          </div>

          {/* Debate Log */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {combinedLog.map((entry, index) => {
              const isUserEntry = entry.role === 'user';
              const isChair = entry.role === 'chair' || entry.type === 'chair-announcement';
              const isProcedural = entry.type === 'motion' || entry.type === 'point';
              const isDelegate = entry.role === 'bot' && entry.delegateName;
              
              return (
                <div key={index} className={`mb-4 p-3 rounded-lg ${
                  isUserEntry 
                    ? 'bg-blue-100 dark:bg-blue-900/30 ml-8' 
                    : isDelegate
                    ? 'bg-purple-100 dark:bg-purple-900/30 mr-8'
                    : entry.role === 'bot' 
                    ? 'bg-gray-100 dark:bg-gray-700 mr-8' 
                    : isChair
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600'
                    : isProcedural
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-center'
                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-center'
                }`}>
                  <div className="flex items-start">
                    {isUserEntry && <Mic className="w-5 h-5 mr-2 mt-0.5 text-blue-600" />}
                    {isDelegate && <User className="w-5 h-5 mr-2 mt-0.5 text-purple-600" />}
                    {entry.role === 'bot' && !isProcedural && !isChair && !isDelegate && <MessageSquare className="w-5 h-5 mr-2 mt-0.5 text-gray-600" />}
                    {isChair && <Gavel className="w-5 h-5 mr-2 mt-0.5 text-yellow-600" />}
                    {isProcedural && !isChair && <Gavel className="w-5 h-5 mr-2 mt-0.5 text-yellow-600" />}
                    <div className="flex-1">
                      {isDelegate && (
                        <div className="text-xs font-semibold text-purple-800 dark:text-purple-300 mb-1">
                          {entry.delegateName} ({entry.delegateState})
                          {entry.delegatePosition && (
                            <span className="ml-2 text-[10px] text-purple-600 dark:text-purple-400">
                              {entry.delegatePosition}
                            </span>
                          )}
                        </div>
                      )}
                      {isChair && (
                        <div className="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-1 uppercase">
                          Chair
                        </div>
                      )}
                      {entry.type && !isChair && (
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase">
                          {entry.type === 'motion' ? 'Motion' : entry.type === 'point' ? 'Point' : ''}
                        </div>
                      )}
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{entry.text}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            {error && (
              <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded">
                {error}
              </div>
            )}
            
            <div className="flex space-x-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  currentPhase === 'motion-to-consider' 
                    ? "Move to consider the bill..." 
                    : currentPhase === 'structured-debate'
                    ? `Deliver your ${getStructuredDebateLabel().toLowerCase()}...`
                    : currentPhase === 'general-debate'
                    ? "Deliver your speech (2 minutes)..."
                    : currentPhase === 'amendments'
                    ? "Debate the amendment..."
                    : "Type your response..."
                }
                disabled={loading}
                rows={3}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                onClick={() => {
                  if (currentPhase === 'structured-debate') {
                    handleStructuredSpeechComplete();
                  }
                  sendTurn();
                }}
                disabled={!message.trim() || loading}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Motion Modal */}
      {showMotionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Propose Motion</h3>
              <button
                onClick={() => {
                  setShowMotionModal(false);
                  setSelectedMotion(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {pmcMotions
                .filter(motion => {
                  // Filter motions based on current phase
                  if (motion.availableIn.includes('all')) return true;
                  return motion.availableIn.includes(currentPhase);
                })
                .map((motion) => (
                  <button
                    key={motion.id}
                    onClick={() => handleMotion(motion)}
                    className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{motion.name}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{motion.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Requires: {motion.requires}</div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Point Modal */}
      {showPointModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Raise Point</h3>
              <button
                onClick={() => {
                  setShowPointModal(false);
                  setSelectedPoint(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {pmcPoints.map((point) => (
                <button
                  key={point.id}
                  onClick={() => handlePoint(point)}
                  className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="font-medium text-gray-900 dark:text-white">{point.name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{point.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Amendment Form Modal */}
      {showAmendmentForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Propose Amendment</h3>
              <button
                onClick={() => {
                  setShowAmendmentForm(false);
                  setAmendmentText('');
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={amendmentText}
              onChange={(e) => setAmendmentText(e.target.value)}
              placeholder="Enter your amendment text..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAmendmentForm(false);
                  setAmendmentText('');
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAmendment}
                disabled={!amendmentText.trim()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Amendment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voting Interface */}
      {isVoting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Vote on Bill</h3>
              <button
                onClick={() => setIsVoting(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {!voteResult ? (
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setVoteResult('yea');
                    addChairMessage('The delegate has voted Yea. All votes have been cast. The bill has been passed/failed by the committee.');
                  }}
                  className="w-full p-4 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 font-medium flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Yea
                </button>
                <button
                  onClick={() => {
                    setVoteResult('nay');
                    addChairMessage('The delegate has voted Nay. All votes have been cast. The bill has been passed/failed by the committee.');
                  }}
                  className="w-full p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 font-medium flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  Nay
                </button>
                <button
                  onClick={() => {
                    setVoteResult('abstain');
                    addChairMessage('The delegate has abstained. All votes have been cast. The bill has been passed/failed by the committee.');
                  }}
                  className="w-full p-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 font-medium"
                >
                  Abstain
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-2">You voted: <strong className="capitalize">{voteResult}</strong></p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  This Model Congress debate session is complete. You can start a new debate to debate another bill.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => {
                      setIsVoting(false);
                      setVoteResult(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Close
                  </button>
                  {onEndDebate && (
                    <button
                      onClick={() => {
                        setIsVoting(false);
                        onEndDebate();
                      }}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md"
                    >
                      End Debate & Start New
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelCongressLayout;
